import { Flex, Table, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing, textColor } from '../../theming';
import { BATCHES_PAGE_SIZES, formatLocal } from '../../utils/vinExtraction';
import { monoTextStyle, stuckRowBackground } from './vinExtractionStyles';
import { useNumberFormat } from './useNumberFormat';
import NoValue from './NoValue';
import BatchJobName from './BatchJobName';
import BatchResultCell from './BatchResultCell';
import BatchCarsPanel from './BatchCarsPanel';

const countStyle = color => ({ color, fontWeight: 500 });

const useColumns = () => {
    const { t, i18n } = useTranslation('vinExtraction');
    const { integer } = useNumberFormat();
    const counted = (key, color) => ({
        key,
        title: t(`batches.columns.${key}`),
        align: 'right',
        render: (_, batch) => <span style={countStyle(color)}>{integer(batch.current[key])}</span>,
    });

    return [
        {
            key: 'created',
            title: t('batches.columns.created'),
            render: (_, batch) => formatLocal(batch.createdAt, 'DD MMM HH:mm', i18n.language),
        },
        { key: 'job', title: t('batches.columns.job'), render: (_, batch) => <BatchJobName jobName={batch.jobName} /> },
        {
            key: 'model',
            title: t('batches.columns.model'),
            render: (_, batch) => (
                <Flex align="center" gap={spacing[1]}>
                    {batch.model ? (
                        <span style={monoTextStyle}>{batch.model}</span>
                    ) : (
                        <NoValue label={t('batches.noModel')} />
                    )}
                    {batch.manual === true && (
                        <Tooltip title={t('batches.manualHelp')}>
                            <Tag color="blue">{t('batches.manual')}</Tag>
                        </Tooltip>
                    )}
                </Flex>
            ),
        },
        {
            key: 'cars',
            title: t('batches.columns.cars'),
            align: 'right',
            render: (_, batch) => integer(batch.carCount),
        },
        { key: 'result', title: t('batches.columns.result'), render: (_, batch) => <BatchResultCell batch={batch} /> },
        {
            key: 'currentStatus',
            title: t('batches.columns.currentStatus'),
            children: [
                counted('success', textColor.success),
                counted('failed', textColor.error),
                counted('notFound', textColor.secondaryAlt),
            ],
        },
    ];
};

/** The batches table; a row expands into its cars panel, a stuck batch's row is tinted (E21). */
const BatchesTable = ({ data, loading, page, pageSize, refreshToken, settings, onPageChange }) => {
    const { t } = useTranslation('vinExtraction');
    const columns = useColumns();

    return (
        <Table
            size="middle"
            rowKey="id"
            columns={columns}
            dataSource={data.items}
            loading={loading}
            scroll={{ x: 'max-content' }}
            onRow={batch => (batch.stuck ? { style: { backgroundColor: stuckRowBackground } } : {})}
            expandable={{
                expandedRowRender: batch => (
                    <BatchCarsPanel
                        batchId={batch.id}
                        refreshToken={refreshToken}
                        currentThreshold={settings?.confidenceThreshold ?? null}
                    />
                ),
            }}
            pagination={{
                current: page,
                pageSize,
                total: data.total,
                showSizeChanger: true,
                pageSizeOptions: BATCHES_PAGE_SIZES,
                showTotal: (total, [from, to]) => t('batches.pagination', { from, to, total }),
                onChange: (nextPage, nextPageSize) =>
                    onPageChange({ page: nextPageSize === pageSize ? nextPage : 1, pageSize: nextPageSize }),
            }}
        />
    );
};

export default BatchesTable;
